import { useMemo, useState } from "react";
import { Quote, BadgeCheck } from "lucide-react";
import { Stars, GoogleG } from "./Stars";
import { fmtDate } from "@/lib/api";

const Banner = () => (
  <div className="rounded-3xl bg-teal text-white p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-5 grain" data-testid="reviews-banner">
    <div className="flex items-center gap-4">
      <span className="h-14 w-14 rounded-2xl bg-white grid place-items-center"><GoogleG className="h-8 w-8" /></span>
      <div>
        <p className="text-xs font-semibold tracking-[0.25em] text-mint">EXCELLENT</p>
        <p className="font-display text-3xl sm:text-4xl font-semibold flex items-center gap-3">5.0 <Stars size={22} /></p>
      </div>
    </div>
    <p className="text-white/85 text-sm sm:text-base">Based on <strong className="text-white">300+ Verified Google Reviews</strong></p>
  </div>
);

export const Reviews = ({ reviews }) => {
  const [tag, setTag] = useState("All");
  const tags = useMemo(() => ["All", ...new Set(reviews.map((r) => r.treatment_tag))], [reviews]);
  const shown = tag === "All" ? reviews : reviews.filter((r) => r.treatment_tag === tag);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24" id="reviews" data-testid="reviews-section">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-light mb-3">Patient stories</p>
      <h2 className="text-3xl sm:text-4xl font-semibold text-ink mb-8 max-w-2xl">Why Sainikpuri trusts Smileton Dental</h2>
      <Banner />
      <div className="flex gap-2 overflow-x-auto no-scrollbar mt-8 pb-1" data-testid="review-filters">
        {tags.map((t) => (
          <button key={t} type="button" onClick={() => setTag(t)} data-testid={`review-filter-${t.toLowerCase().replace(/\s+/g, "-")}`}
            className={`shrink-0 rounded-full px-4 py-2 text-sm font-medium border transition-colors ${tag === t ? "bg-teal text-white border-teal" : "bg-white text-ink border-slate-200 hover:border-teal/40"}`}>
            {t}
          </button>
        ))}
      </div>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {shown.map((r, i) => (
          <article key={r.id} className="reveal rounded-3xl bg-white border border-slate-200/80 p-6 flex flex-col hover:-translate-y-1 transition-transform duration-300" style={{ animationDelay: `${i * 60}ms` }} data-testid="review-card">
            <div className="flex items-center justify-between">
              <Stars size={15} />
              <span className="rounded-full bg-mint text-teal text-[11px] font-semibold px-2.5 py-1">{r.treatment_tag}</span>
            </div>
            <Quote className="h-6 w-6 text-mint-border mt-4" />
            <p className="mt-2 text-[15px] leading-relaxed text-ink flex-1">{r.review_text}</p>
            <div className="mt-5 flex items-center justify-between text-sm">
              <span className="font-semibold text-ink inline-flex items-center gap-1">{r.patient_name}<BadgeCheck className="h-4 w-4 text-teal" /></span>
              <span className="text-ink-muted text-xs">{fmtDate(r.review_date, { month: "short", year: "numeric" })}</span>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};
